import { computed, onUnmounted, ref, type ComputedRef } from "vue";
import { io as ioClient, Socket } from "socket.io-client";
import { markLicenseRequired } from "@/shared/composables/useLicenseGate";
import {
	clearSpaceRefusal,
	markSpaceRefused,
	type RefusedSpaceKind,
	type SpaceRefusalCode,
} from "@/shared/composables/useSpaceRestriction";

/*
  The client end of live sync — see docs/live-sync.md for the protocol.

  Every event the server sends carries `room` and `seq`: its room's sequence number, one higher
  than the room's previous event. That makes a lost packet detectable, and this module is where it
  is detected:

  - An event at or below the room's last number is a duplicate (a replay overlapping what already
    arrived) and is dropped.
  - An event past the next number means something was lost. The room is RESYNCED: `onResync`
    listeners refetch what they show, and a dataspace room is re-joined for a fresh snapshot, with
    its events held back until that snapshot arrives.
  - The socket excluded from an event (the tab that made the change) gets `room:seq` instead,
    which advances the number and is otherwise invisible.
  - Every few seconds each room sends `room:head`: where its numbers stand, without taking one.
    A room we stand behind lost its last event — the one no later event would have revealed — and
    is resynced.

  A join can also be refused for what a license takes from the space: a project whose Designer is
  closed (`designer_unavailable`), a dataspace that is closed to the Operator (`space_locked`), each
  with its `reason`. That is told to `useSpaceRestriction`, for the space the room is, and the room
  stays wanted: a reconnect asks again, and `rejoinRoom` asks when the space was read again and the
  restriction is gone. A room the socket is out of hears nothing from the space, so what tells it
  the space reopened is `space:access:changed` in the user's own room (`useSpaceReopening`).

  Joins are acknowledged, and a project room resumes from its last number: the server replays what
  was missed if it still can, and otherwise the ack says to resync. A reconnect re-joins every room
  that way. A department's room asks the same way, but the server keeps no ring for it: a join that
  says it missed anything is answered `resync`, and its page refetches. A dataspace join is answered
  with one `dataspace:snapshot`, which the Operator's stream
  (`operator/composables/process/useProcessStream.ts`) rebuilds its copy from.

  A department's dataspace limits how many people may have its processes open at once, and a person
  who opens a process view — to act in it or only to watch — claims a seat for it over the socket (`seat:claim`, see `holdSeat`). The claim is
  the socket's own: the server ends it when the socket disconnects or is taken out of the
  dataspace's room, so a reconnect — and a room asked for again after a refusal — claims it again.

  Handlers register HERE, not on the socket, so a subscription made before the socket exists, or
  kept across a reconnect that replaced it, still fires.
*/

type Payload = Record<string, unknown>;
type EventHandler = (payload: Payload) => void;
type ResyncHandler = (room: string) => void;

/** The sequence-number-only notice the originating socket gets for its own change. */
const SEQ_EVENT = "room:seq";
/** The heartbeat: `{ room, seq }`, the room's last number. Consumes none. */
const HEAD_EVENT = "room:head";
const SNAPSHOT_EVENT = "dataspace:snapshot";

let socket: Socket | null = null;
/** Set by the first `connect`; a later one is a reconnect. */
let hasConnected = false;

const handlers = new Map<string, Set<EventHandler>>();
const resyncHandlers = new Set<ResyncHandler>();

interface RoomState {
	/** The last sequence number applied; undefined until the room's first event or ack. */
	seq?: number;
	/** The server run the numbers belong to, from the join ack. */
	epoch?: string;
	/** A dataspace room waiting for the snapshot its join asked for; its events are held back. */
	awaitingSnapshot?: boolean;
	/**
	 * The socket is out of a room it holds: its last join was refused, or the server took it out
	 * (`markRoomLost`). The room is still wanted, and is asked for again (`rejoinRoom`).
	 */
	refused?: boolean;
	/** A join is sent and not answered yet, so `rejoinRoom` does not send a second. */
	asking?: boolean;
}
const rooms = new Map<string, RoomState>();

interface Membership {
	refs: number;
	/** A leave waiting for the end of the tick, in case the room is joined again first. */
	pendingLeave?: ReturnType<typeof setTimeout>;
}
const joined = new Map<string, Membership>();

// ── Connection ─────────────────────────────────────────────────────────────

interface SocketAuthHooks {
	getToken: () => string | null;
	ensureSession: () => Promise<boolean>;
	login: (returnTo?: string) => void;
}

// Injected instead of imported: useAuth depends on api.ts, which depends on this
// module, so importing it here would close an import cycle.
let authHooks: SocketAuthHooks | null = null;

export function configureSocketAuth(hooks: SocketAuthHooks): void {
	authHooks = hooks;
}

// Codes after which only a fresh sign-in can help. Defined here, not in api.ts, because
// api.ts already imports this module and the reverse import would be a cycle.
export const REAUTH_CODES = new Set([
	"revoked",
	"unauthenticated",
	"legacy_token",
	"account_suspended",
]);

export function connectSocket(): void {
	// Never a second socket. One mid-reconnect is still ours; it comes back by itself.
	if (socket) {
		if (!socket.connected) socket.connect();
		return;
	}
	if (!authHooks) return;
	const hooks = authHooks;

	const s = ioClient("/", {
		// A function, so every (re)connect presents the CURRENT token — refreshed in memory — rather
		// than the one the socket was created with.
		auth: (cb) => cb({ token: hooks.getToken() }),
		transports: ["websocket", "polling"],
	});
	socket = s;

	// The server closes the connection on token expiry, on logout and on a ban. socket.io does not
	// auto-reconnect after a server-initiated disconnect, so renew the session first.
	s.on("disconnect", (reason) => {
		if (reason !== "io server disconnect") return;
		void hooks.ensureSession().then((ok) => {
			if (ok) s.connect();
			else hooks.login();
		});
	});

	// `err.message` carries the server's bare code. Transient codes (idp_unavailable,
	// internal) fall through to socket.io's own backoff retry.
	s.on("connect_error", (err) => {
		if (!REAUTH_CODES.has(err.message)) return;
		// Stop socket.io's retry so a server that keeps refusing (e.g. clock skew
		// making a fresh token look invalid) cannot bounce the page through the IdP
		// repeatedly. `login` itself only navigates once per page load.
		s.disconnect();
		hooks.login();
	});

	s.on("connect", () => {
		const reconnect = hasConnected;
		hasConnected = true;
		for (const room of joined.keys()) sendJoin(room);
		// The server ended this socket's claims with the last connection, so every held seat is claimed
		// again — and may now be refused, if somebody else took it while the person was away.
		for (const dataspaceId of seatHolds.keys()) sendSeatClaim(dataspaceId);
		if (reconnect) {
			// Rooms the server joins by itself (`user:`) cannot be resumed: whatever they missed while
			// we were away is gone, so they resync. Joined rooms resync only if their ack says so.
			for (const room of rooms.keys()) {
				if (!joined.has(room)) resync(room);
			}
		}
	});
	s.onAny(receive);
}

export function disconnectSocket(): void {
	socket?.disconnect();
	socket = null;
	hasConnected = false;
	rooms.clear();
	for (const membership of joined.values()) clearTimeout(membership.pendingLeave);
	joined.clear();
	releases.clear();
	for (const hold of seatHolds.values()) clearTimeout(hold.pendingRelease);
	seatHolds.clear();
	seatStates.value = {};
}

export function getSocketId(): string | null {
	return socket?.id ?? null;
}

// ── Rooms ──────────────────────────────────────────────────────────────────

/**
 * Joins `room` (`project:{id}`, `department:{id}` or `dataspace:{id}`) and returns the release. Reference-counted: the
 * room is joined on the first hold and left on the last release — and a release followed by a new
 * hold in the same tick (one view unmounting as the next mounts) sends nothing at all.
 */
export function holdRoom(room: string): () => void {
	const membership = joined.get(room);
	if (membership) {
		membership.refs += 1;
		if (membership.pendingLeave) {
			clearTimeout(membership.pendingLeave);
			membership.pendingLeave = undefined;
		}
	} else {
		joined.set(room, { refs: 1 });
		sendJoin(room);
	}
	let released = false;
	return () => {
		if (released) return;
		released = true;
		releaseRoom(room);
	};
}

function releaseRoom(room: string): void {
	const membership = joined.get(room);
	if (!membership) return;
	membership.refs -= 1;
	if (membership.refs > 0) return;
	membership.pendingLeave = setTimeout(() => {
		if (joined.get(room) !== membership || membership.refs > 0) return;
		joined.delete(room);
		rooms.delete(room);
		const [kind, id] = splitRoom(room);
		socket?.emit(`leave:${kind}`, id);
	}, 0);
}

function sendJoin(room: string): void {
	if (!socket?.connected) return; // the `connect` handler joins it
	const [kind, id] = splitRoom(room);
	const state = roomState(room);
	// A project room replays what was missed; a department's cannot, but says so (`resync`) rather
	// than leaving the client to compare numbers, so it asks the same way.
	const request =
		(kind === "project" || kind === "department") && state.seq !== undefined
			? { id, since: state.seq, epoch: state.epoch }
			: { id };
	if (kind === "dataspace") state.awaitingSnapshot = true;
	state.asking = true;
	socket.emit(`join:${kind}`, request, (ack: Payload) => onJoinAck(room, ack));
}

function onJoinAck(room: string, ack: Payload): void {
	const [kind, id] = splitRoom(room);
	const state = roomState(room);
	state.asking = false;
	if (!ack?.ok) {
		// The Designer's and the Operator's rooms are gated by the license like their routes.
		if (ack?.code === "license_required") markLicenseRequired();
		refuseJoin(kind, id, ack);
		// No snapshot is on its way: events must not be held back for one.
		state.awaitingSnapshot = false;
		state.refused = true;
		return;
	}
	// Accepted, so whatever was refused before is not so any more.
	const reopened = state.refused === true;
	state.refused = false;
	if (kind === "project" || kind === "dataspace") clearSpaceRefusal(kind, id);
	// Taken out of the room, the socket's claim on the dataspace ended with it (the server's eviction).
	if (reopened && kind === "dataspace") sendSeatClaim(id);
	if (settleJoin(room, state, ack)) return;
	// A project room that was refused and is joined now missed what happened in between, which a
	// fresh join does not replay: whoever reads it reads it again. A dataspace room's snapshot says it.
	if (reopened && kind !== "dataspace") resync(room);
}

/** A refused join tells `useSpaceRestriction` when the refusal is a restriction of the space. */
function refuseJoin(kind: string, id: string, ack?: Payload): void {
	const code = ack?.code;
	const restricts =
		(kind === "project" && code === "designer_unavailable") ||
		(kind === "dataspace" && code === "space_locked");
	if (!restricts) return;
	markSpaceRefused({
		kind: kind as RefusedSpaceKind,
		id,
		code: code as SpaceRefusalCode,
		reason: typeof ack?.reason === "string" ? ack.reason : "",
	});
}

/** Reconciles the room's numbers with an accepted join's ack. True when it asked for a resync. */
function settleJoin(room: string, state: RoomState, ack: Payload): boolean {
	state.epoch = ack.epoch as string | undefined;
	const head = ack.seq as number;
	if (ack.resync) {
		state.seq = head;
		resync(room);
		return true;
	}
	if (state.seq === undefined) {
		state.seq = head;
		return false;
	}
	// A resume: the replay arrived before the ack, so we should now stand where the room does.
	if (state.seq !== head) {
		state.seq = head;
		resync(room);
		return true;
	}
	return false;
}

/**
 * Asks again for a held room that this socket is out of: its join was refused, or the server took it
 * out (`markRoomLost`) — and the space was read again and what refused it is gone (a member removed,
 * a license renewed, the space moved). Nothing is sent for a room that is not held, that the
 * socket is in, or that is being asked for already.
 */
export function rejoinRoom(room: string): void {
	const state = rooms.get(room);
	if (joined.has(room) && state?.refused && !state.asking) sendJoin(room);
}

/**
 * A dataspace that has just been closed to the Operator takes its sockets out of the room without a
 * word to them, and refuses the next join. A client that learns the dataspace is closed says so
 * here, so the room is asked for again (`rejoinRoom`) once it is open — nothing is sent now, since
 * the join would only be refused. A room that is not held is not marked.
 */
export function markRoomLost(room: string): void {
	if (joined.has(room)) roomState(room).refused = true;
}

/**
 * Whether the socket is out of a room it holds — its join was refused, or `markRoomLost` says the
 * server took it out — and so cannot hear what happens there. For `useSpaceReopening`: such a
 * session learns that the space opened again from the user's own room instead.
 */
export function isRoomRefused(room: string): boolean {
	return joined.has(room) && rooms.get(room)?.refused === true;
}

/** Asks for a fresh copy of `room`: its `onResync` listeners refetch, and a dataspace re-joins. */
export function resyncRoom(room: string): void {
	resync(room);
}

function resync(room: string): void {
	if (room.startsWith("dataspace:") && joined.has(room)) sendJoin(room);
	for (const handler of [...resyncHandlers]) safely(() => handler(room));
}

// ── Seats ──────────────────────────────────────────────────────────────────

/** What a claim came to: the seat the person holds, which a personal dataspace has none of (`none`). */
export type HeldSeat = "named" | "floating" | "none";

/**
 * The server's answer to a `seat:claim`.
 *
 *  - `granted`: the person holds `seat` in the dataspace's department (`none`: it is personal).
 *  - `refused`: every floating seat of the department is taken (`seat_unavailable`), `limit` being how
 *    many it has. Nothing is held; asking again once a seat has freed up may succeed.
 *  - `failed`: anything else — `forbidden` (the person cannot reach the dataspace), `space_locked` and
 *    `license_required` (said to their own gates, as a join's refusal is), `bad_request`, `internal`.
 *    None of these is about a seat, so a page does not stand in for itself on them.
 */
export type SeatOutcome =
	| { status: "granted"; seat: HeldSeat }
	| { status: "refused"; departmentId: string; limit: number }
	| { status: "failed"; code: string };

interface SeatHolds {
	refs: number;
	/** The newest claim sent; an answer to an older one — before a reconnect, a retry — is dropped. */
	attempt: number;
	/** A release waiting for the end of the tick, in case the seat is held again first. */
	pendingRelease?: ReturnType<typeof setTimeout>;
}
const seatHolds = new Map<string, SeatHolds>();

interface SeatState {
	/** The latest answer, kept while the next is asked for, so a reconnect does not flash the page. */
	outcome: SeatOutcome | null;
	/** A claim is sent and not answered yet. */
	asking: boolean;
}
/** By dataspace id; what a hold's `outcome` and `asking` read. */
const seatStates = ref<Record<string, SeatState>>({});

function setSeatState(dataspaceId: string, change: Partial<SeatState>): void {
	const held = seatStates.value[dataspaceId] ?? { outcome: null, asking: false };
	seatStates.value = { ...seatStates.value, [dataspaceId]: { ...held, ...change } };
}

/** What one hold of a seat exposes. Every holder of a dataspace shares one claim and one answer. */
export interface SeatHold {
	/** The latest answer to the claim; `null` until the first one comes. */
	outcome: ComputedRef<SeatOutcome | null>;
	/** A claim is out and not answered yet, so a "Try again" can show it is working. */
	asking: ComputedRef<boolean>;
	/** Asks again — what a person does after a `refused`, once a seat may have freed up. */
	retry: () => void;
	/** Lets go of this hold; the last one sends `seat:release`. Idempotent. */
	release: () => void;
}

/**
 * Claims a seat in `dataspaceId` for as long as the hold lasts, and returns its answer.
 *
 * A seat is for ACTING, so what holds one is a view that lets the person act (the process view),
 * not any room: the claim is sent over the socket (`seat:claim { dataspace_id }`), answered with
 * the seat or a refusal (`SeatOutcome`), and given back with `seat:release`. Reference-counted as
 * `holdRoom` is: the first hold claims, the last release lets go — and a release followed by a new
 * hold in the same tick (one view unmounting as the next mounts) sends nothing at all. The claim is
 * made again after a reconnect and after the dataspace's room is joined again, since the server
 * ended it with the connection or the eviction; the seat is kept for two minutes after it ended, so
 * the person does not lose it by that.
 */
export function holdSeat(dataspaceId: string): SeatHold {
	const existing = seatHolds.get(dataspaceId);
	if (existing) {
		existing.refs += 1;
		clearTimeout(existing.pendingRelease);
		existing.pendingRelease = undefined;
	} else {
		seatHolds.set(dataspaceId, { refs: 1, attempt: 0 });
		sendSeatClaim(dataspaceId);
	}
	let released = false;
	return {
		outcome: computed(() => seatStates.value[dataspaceId]?.outcome ?? null),
		asking: computed(() => seatStates.value[dataspaceId]?.asking ?? false),
		retry: () => {
			if (!released) sendSeatClaim(dataspaceId);
		},
		release: () => {
			if (released) return;
			released = true;
			releaseSeat(dataspaceId);
		},
	};
}

function releaseSeat(dataspaceId: string): void {
	const hold = seatHolds.get(dataspaceId);
	if (!hold) return;
	hold.refs -= 1;
	if (hold.refs > 0) return;
	hold.pendingRelease = setTimeout(() => {
		if (seatHolds.get(dataspaceId) !== hold || hold.refs > 0) return;
		seatHolds.delete(dataspaceId);
		const { [dataspaceId]: _gone, ...rest } = seatStates.value;
		seatStates.value = rest;
		// Only on a live connection: one that dropped has nothing to release (the server ended the
		// claims with it), and a release buffered over a gap would arrive after the next claim.
		if (socket?.connected) socket.emit("seat:release", { dataspace_id: dataspaceId });
	}, 0);
}

function sendSeatClaim(dataspaceId: string): void {
	const hold = seatHolds.get(dataspaceId);
	// Not connected: the `connect` handler claims every seat that is held.
	if (!hold || hold.refs < 1 || !socket?.connected) return;
	const attempt = ++hold.attempt;
	setSeatState(dataspaceId, { asking: true });
	socket.emit("seat:claim", { dataspace_id: dataspaceId }, (ack: Payload) =>
		onSeatAck(dataspaceId, hold, attempt, ack),
	);
}

function onSeatAck(dataspaceId: string, hold: SeatHolds, attempt: number, ack: Payload): void {
	// Let go of since it was sent, or overtaken by a newer claim: not about the seat held now.
	if (seatHolds.get(dataspaceId) !== hold || hold.attempt !== attempt) return;
	const outcome = seatOutcome(ack);
	if (outcome.status === "failed") {
		// What a join of the dataspace's room would have said of the same thing.
		if (outcome.code === "license_required") markLicenseRequired();
		refuseJoin("dataspace", dataspaceId, ack);
	}
	setSeatState(dataspaceId, { outcome, asking: false });
}

function seatOutcome(ack: Payload | undefined): SeatOutcome {
	if (ack?.ok) {
		const seat = ack.seat === "named" || ack.seat === "floating" ? ack.seat : "none";
		return { status: "granted", seat };
	}
	if (ack?.code === "seat_unavailable") {
		return {
			status: "refused",
			departmentId: typeof ack.department_id === "string" ? ack.department_id : "",
			limit: typeof ack.limit === "number" ? ack.limit : 0,
		};
	}
	return { status: "failed", code: typeof ack?.code === "string" ? ack.code : "internal" };
}

// ── Receiving ──────────────────────────────────────────────────────────────

function receive(event: string, payload?: unknown): void {
	const data = (payload ?? {}) as Payload;
	const room = typeof data.room === "string" ? data.room : undefined;
	const seq = typeof data.seq === "number" ? data.seq : undefined;
	if (event === HEAD_EVENT) {
		if (room !== undefined && seq !== undefined) atHead(room, seq);
		return;
	}
	if (room !== undefined && seq !== undefined && !accept(room, seq, event)) return;
	if (event === SEQ_EVENT) return;
	dispatch(event, data);
}

/** Whether an event numbered `seq` in `room` is new — and resyncs the room if one was skipped. */
function accept(room: string, seq: number, event: string): boolean {
	const state = roomState(room);
	if (event === SNAPSHOT_EVENT) {
		state.awaitingSnapshot = false;
		state.seq = seq;
		return true;
	}
	if (state.awaitingSnapshot) return false; // covered by, or after, the snapshot on its way
	if (state.seq === undefined) {
		state.seq = seq;
		return true;
	}
	if (seq <= state.seq) return false;
	const skipped = seq > state.seq + 1;
	state.seq = seq;
	if (!skipped) return true;
	resync(room);
	// A dataspace's fresh snapshot will include this event; anything else applies it as well.
	return !(room.startsWith("dataspace:") && joined.has(room));
}

/**
 * The room says its last number is `head`. Behind it: the last event (or several) never arrived,
 * and nothing after them would have told us — resync. At it: nothing to do. A room we do not hold
 * (a heartbeat racing our leave) is ignored, and so is one whose snapshot is on its way.
 */
function atHead(room: string, head: number): void {
	const held = joined.has(room) || room.startsWith("user:");
	if (!held) return;
	const state = roomState(room);
	if (state.awaitingSnapshot) return;
	// The user's own room is joined by the server, with no ack to say where it stood: the first
	// head is where we start counting.
	if (state.seq === undefined) {
		state.seq = head;
		return;
	}
	if (head <= state.seq) return;
	state.seq = head;
	resync(room);
}

function dispatch(event: string, payload: Payload): void {
	for (const handler of [...(handlers.get(event) ?? [])]) safely(() => handler(payload));
}

// ── Subscribing ────────────────────────────────────────────────────────────

/** Calls `handler` for every `event`, until the returned function is called. */
export function onSocketEvent(event: string, handler: EventHandler): () => void {
	let set = handlers.get(event);
	if (!set) handlers.set(event, (set = new Set()));
	set.add(handler);
	return () => set.delete(handler);
}

/** Calls `handler` with a room's name whenever that room has to be refetched: a skipped sequence
 *  number, a resume the server could not serve, or a reconnect. */
export function onResync(handler: ResyncHandler): () => void {
	resyncHandlers.add(handler);
	return () => resyncHandlers.delete(handler);
}

/** `onSocketEvent` for a component: unsubscribed when it unmounts. */
function useEvents() {
	return (event: string, handler: EventHandler) => {
		onUnmounted(onSocketEvent(event, handler));
	};
}

/**
 * Subscribe to project-scoped socket events from within a component.
 * Does NOT manage room membership — the layout owns join/leave.
 */
export function useProjectEvents() {
	return { onProjectEvent: useEvents() };
}

/**
 * Subscribe to dataspace-scoped socket events from within a component.
 * Does NOT manage room membership — the view owns join/leave.
 */
export function useDataspaceEvents() {
	return { onDataspaceEvent: useEvents() };
}

/**
 * Subscribe to user-scoped socket events (personal notifications).
 * The server adds the socket to `user:{uid}` automatically on connect.
 */
export function useUserEvents() {
	return { onUserEvent: useEvents() };
}

// ── The room API the layouts and views already call ─────────────────────────

/** The releases `joinX` took, so `leaveX` can hand one back. */
const releases = new Map<string, Array<() => void>>();

function hold(room: string): void {
	releases.set(room, [...(releases.get(room) ?? []), holdRoom(room)]);
}

function release(room: string): void {
	const held = releases.get(room);
	held?.pop()?.();
	if (held && held.length === 0) releases.delete(room);
}

/** Called by DesignerLayout when the active project changes. */
export function joinProject(projectId: string): void {
	hold(`project:${projectId}`);
}

/** Called by DesignerLayout when leaving a project. */
export function leaveProject(projectId: string): void {
	release(`project:${projectId}`);
}

/** Called by the department's pages while one is open (`useDepartmentSync`). */
export function joinDepartment(departmentId: string): void {
	hold(`department:${departmentId}`);
}

/** Called by the department's pages when leaving it. */
export function leaveDepartment(departmentId: string): void {
	release(`department:${departmentId}`);
}

/** Called by operator views when entering a dataspace. */
export function joinDataspace(dataspaceId: string): void {
	hold(`dataspace:${dataspaceId}`);
}

/** Called by operator views when leaving a dataspace. */
export function leaveDataspace(dataspaceId: string): void {
	release(`dataspace:${dataspaceId}`);
}

// ── Internals ──────────────────────────────────────────────────────────────

function roomState(room: string): RoomState {
	let state = rooms.get(room);
	if (!state) rooms.set(room, (state = {}));
	return state;
}

function splitRoom(room: string): [string, string] {
	const i = room.indexOf(":");
	return [room.slice(0, i), room.slice(i + 1)];
}

function safely(run: () => void): void {
	try {
		run();
	} catch (error) {
		console.error("[socket] a handler failed", error);
	}
}
